import { useUiText } from '../lib/ui-text';
import { useId, useState } from 'react';
import { Button } from './button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './dialog';
import { Input } from './input';
import { Label } from './label';

export interface TeamDetailsValues {
  name: string;
  displayName: string;
}

export function TeamDetailsEditor({
  team,
  onSave,
}: {
  team: { displayName: string; slug: string };
  onSave: (values: TeamDetailsValues) => Promise<unknown>;
}) {
  const uiText = useUiText();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState(team.displayName);
  const [slug, setSlug] = useState(team.slug);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ key: 'saveTeamError' } | { detail: string } | null>(null);

  return (
    <Dialog open={open} onOpenChange={value => !saving && setOpen(value)}>
      <Button
        variant="outline"
        size="sm"
        aria-label={uiText('editName', { name: team.displayName })}
        onClick={() => {
          setDisplayName(team.displayName);
          setSlug(team.slug);
          setError(null);
          setOpen(true);
        }}
      >
        {uiText('edit')}
      </Button>
      <DialogContent className="rounded-3xl">
        <DialogHeader>
          <DialogTitle>{uiText('editTeam')}</DialogTitle>
          <DialogDescription>{uiText('changeTheTeamSNameAndSlug')}</DialogDescription>
        </DialogHeader>
        <form
          aria-label={uiText('editTeam')}
          className="space-y-4"
          onSubmit={async event => {
            event.preventDefault();
            if (saving || !displayName.trim() || !slug.trim()) return;
            setSaving(true);
            setError(null);
            try {
              await onSave({ displayName: displayName.trim(), name: slug.trim() });
              setOpen(false);
            } catch (cause) {
              const data = (
                cause as { response?: { data?: { detail?: unknown; error?: unknown } } }
              )?.response?.data;
              const detail = data?.detail ?? data?.error;
              setError(typeof detail === 'string' ? { detail } : { key: 'saveTeamError' });
            } finally {
              setSaving(false);
            }
          }}
        >
          <div className="space-y-2">
            <Label htmlFor={`${id}-name`}>{uiText('teamName')}</Label>
            <Input
              id={`${id}-name`}
              value={displayName}
              onChange={event => setDisplayName(event.target.value)}
              maxLength={255}
              required
              disabled={saving}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${id}-slug`}>{uiText('teamSlug')}</Label>
            <Input
              id={`${id}-slug`}
              value={slug}
              onChange={event => setSlug(event.target.value)}
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              maxLength={255}
              required
              disabled={saving}
              aria-describedby={`${id}-hint`}
            />
            <p id={`${id}-hint`} className="text-sm text-muted-foreground">
              {uiText('useLowercaseLettersNumbersAndHyphensForExampleDerma')}
            </p>
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {'key' in error ? uiText(error.key) : error.detail}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={() => setOpen(false)}
            >
              {uiText('cancel')}
            </Button>
            <Button type="submit" disabled={saving || !displayName.trim() || !slug.trim()}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
